import type { ReactElement, ReactNode } from "react";
import type {
  ProcessTraceParams,
  ProcessTraceStatus,
} from "@playbookai/widget-schemas";
import { Input } from "../../atoms/Input/Input.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { NativeSelect } from "../../primitives/NativeSelect.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSessionMap } from "../field-edit-session.js";
import { PROCESS_TRACE_STATUS_OPTIONS } from "./presentation.js";

/** The two focus callbacks one leaf hangs its edit lease on. */
type FieldLease = {
  readonly onFocus: () => void;
  readonly onBlur: () => void;
};

export function ProcessTraceExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<ProcessTraceParams>): ReactElement {
  const disabled = readOnly === true;
  const commit = (next: ProcessTraceParams): void => {
    if (!disabled) onChange(next);
  };
  /*
   * PB-4923 AC5 — seventeen leaves across two root fields and three nested
   * GROUPS (`por`, `poo`, `poc`), whose target keys are `group/field`. The map
   * form rather than seventeen hooks: the set is fixed, but one call per leaf
   * would bury the form it is meant to describe, and every claim shares the
   * same release rules either way.
   *
   * Each group's `status` is leased alongside its text, deliberately. Status is
   * where two people collide hardest here — the schema requires evidence before
   * `validated`, so an AI flipping a status while somebody is typing the
   * evidence that justifies it invalidates the whole group.
   */
  const lease = useFieldEditSessionMap();

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-5 text-pb-text">
      <header className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">POR → POO → POC</h2>
          <p className="mt-1 text-sm text-pb-text-muted">
            Preserve where the problem was noticed, where behaviour first
            diverged, and where the causal condition can be observed.
          </p>
        </div>
        <p className="ms-auto text-end text-dense-1 text-pb-text-muted">
          {disabled ? "Read-only" : "Changes save automatically"}
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto pe-1">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 pb-1">
          <Input
            label="Trace subject"
            hint="Optional name for this problem or causal path."
            maxLength={255}
            value={data.subject}
            readOnly={disabled}
            {...lease("subject")}
            placeholder="First restart pressure loss after PM"
            onChange={(event) =>
              commit({ ...data, subject: event.target.value })
            }
          />

          <TraceSection
            code="POR"
            title="Point of Recognition"
            description="Where the problem was first noticed."
            status={data.por.status}
            statusLease={lease("por/status")}
            disabled={disabled}
            onStatusChange={(status) =>
              commit({ ...data, por: { ...data.por, status } })
            }
          >
            <Textarea
              label="What was first detected?"
              rows={3}
              maxLength={500}
              value={data.por.observation}
              readOnly={disabled}
              {...lease("por/observation")}
              placeholder="Pressure-loss alarm appears on the first restart"
              onChange={(event) =>
                commit({
                  ...data,
                  por: { ...data.por, observation: event.target.value },
                })
              }
            />
            <div className="grid gap-3 md:grid-cols-2">
              <Input
                label="Recognized at"
                maxLength={255}
                value={data.por.location}
                readOnly={disabled}
                {...lease("por/location")}
                placeholder="M2 operator panel"
                onChange={(event) =>
                  commit({
                    ...data,
                    por: { ...data.por, location: event.target.value },
                  })
                }
              />
              <Textarea
                label="POR evidence"
                rows={2}
                maxLength={500}
                value={data.por.evidence}
                readOnly={disabled}
                {...lease("por/evidence")}
                placeholder="Alarm history and restart log"
                onChange={(event) =>
                  commit({
                    ...data,
                    por: { ...data.por, evidence: event.target.value },
                  })
                }
              />
            </div>
          </TraceSection>

          <TraceSection
            code="POO"
            title="Point of Occurrence"
            description="Where expected and actual behaviour first diverged."
            status={data.poo.status}
            statusLease={lease("poo/status")}
            disabled={disabled}
            onStatusChange={(status) =>
              commit({ ...data, poo: { ...data.poo, status } })
            }
          >
            <Input
              label="Occurrence location"
              maxLength={255}
              value={data.poo.location}
              readOnly={disabled}
              {...lease("poo/location")}
              placeholder="Pressure restoration before restart release"
              onChange={(event) =>
                commit({
                  ...data,
                  poo: { ...data.poo, location: event.target.value },
                })
              }
            />
            <div className="grid gap-3 md:grid-cols-2">
              <Textarea
                label="Expected"
                rows={3}
                maxLength={500}
                value={data.poo.expected}
                readOnly={disabled}
                {...lease("poo/expected")}
                placeholder="Pressure reaches the release threshold"
                onChange={(event) =>
                  commit({
                    ...data,
                    poo: { ...data.poo, expected: event.target.value },
                  })
                }
              />
              <Textarea
                label="Actual"
                rows={3}
                maxLength={500}
                value={data.poo.actual}
                readOnly={disabled}
                {...lease("poo/actual")}
                placeholder="Restart is released below the threshold"
                onChange={(event) =>
                  commit({
                    ...data,
                    poo: { ...data.poo, actual: event.target.value },
                  })
                }
              />
            </div>
            <Textarea
              label="First supported divergence"
              rows={2}
              maxLength={500}
              value={data.poo.divergence}
              readOnly={disabled}
              {...lease("poo/divergence")}
              placeholder="It should be A, but it is B — starting at this step"
              onChange={(event) =>
                commit({
                  ...data,
                  poo: { ...data.poo, divergence: event.target.value },
                })
              }
            />
            <Textarea
              label="POO evidence"
              rows={2}
              maxLength={500}
              value={data.poo.evidence}
              readOnly={disabled}
              {...lease("poo/evidence")}
              placeholder="Standard work and direct observation"
              onChange={(event) =>
                commit({
                  ...data,
                  poo: { ...data.poo, evidence: event.target.value },
                })
              }
            />
          </TraceSection>

          <TraceSection
            code="POC"
            title="Point of Cause"
            description="Where the causal process condition can be observed."
            status={data.poc.status}
            statusLease={lease("poc/status")}
            disabled={disabled}
            onStatusChange={(status) =>
              commit({ ...data, poc: { ...data.poc, status } })
            }
          >
            <Textarea
              label="Observable cause condition"
              rows={3}
              maxLength={500}
              value={data.poc.cause}
              readOnly={disabled}
              {...lease("poc/cause")}
              placeholder="State only the condition that can be observed"
              onChange={(event) =>
                commit({
                  ...data,
                  poc: { ...data.poc, cause: event.target.value },
                })
              }
            />
            <div className="grid gap-3 md:grid-cols-2">
              <Input
                label="Cause location"
                maxLength={255}
                value={data.poc.location}
                readOnly={disabled}
                {...lease("poc/location")}
                placeholder="Valve reset and pressure verification step"
                onChange={(event) =>
                  commit({
                    ...data,
                    poc: { ...data.poc, location: event.target.value },
                  })
                }
              />
              <Textarea
                label="POC evidence"
                rows={2}
                maxLength={500}
                value={data.poc.evidence}
                readOnly={disabled}
                {...lease("poc/evidence")}
                placeholder="Observation, record, or test result"
                onChange={(event) =>
                  commit({
                    ...data,
                    poc: { ...data.poc, evidence: event.target.value },
                  })
                }
              />
            </div>
            <Textarea
              label="Validation method"
              hint="POC is not validated until a direct observation or discriminating test supports it."
              rows={2}
              maxLength={500}
              value={data.poc.validationMethod}
              readOnly={disabled}
              {...lease("poc/validationMethod")}
              placeholder="Test that would validate or falsify this cause"
              onChange={(event) =>
                commit({
                  ...data,
                  poc: { ...data.poc, validationMethod: event.target.value },
                })
              }
            />
          </TraceSection>

          <Textarea
            label="Trace conclusion"
            hint="Summarize the supported chain and preserve any uncertainty."
            rows={3}
            maxLength={500}
            value={data.conclusion}
            readOnly={disabled}
            {...lease("conclusion")}
            placeholder="At step X, it should be A, but it is B — starting there."
            onChange={(event) =>
              commit({ ...data, conclusion: event.target.value })
            }
          />
        </div>
      </div>
    </div>
  );
}

function TraceSection({
  code,
  title,
  description,
  status,
  statusLease,
  disabled,
  onStatusChange,
  children,
}: {
  readonly code: "POR" | "POO" | "POC";
  readonly title: string;
  readonly description: string;
  readonly status: ProcessTraceStatus;
  /** PB-4923 AC5 — the lease for this group's own `status` leaf. */
  readonly statusLease: FieldLease;
  readonly disabled: boolean;
  readonly onStatusChange: (status: ProcessTraceStatus) => void;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section className="rounded-lg border border-pb-border bg-pb-card-elevated/40 p-4">
      <div className="mb-4 flex flex-wrap items-start gap-3">
        <span className="inline-flex h-7 min-w-11 items-center justify-center rounded-md bg-pb-purple-soft px-2 text-xs font-semibold text-pb-purple-text">
          {code}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="mt-0.5 text-xs text-pb-text-muted">{description}</p>
        </div>
        <div className="w-full sm:w-48">
          <NativeSelect<ProcessTraceStatus>
            label={`${code} evidence status`}
            options={PROCESS_TRACE_STATUS_OPTIONS}
            value={status}
            disabled={disabled}
            {...statusLease}
            onChange={onStatusChange}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}
